import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Alert, Card, Row, Col, Spinner, Badge } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTable, faSlidersH, faEye, faCode } from '@fortawesome/free-solid-svg-icons'
import {
  useAdminAnalyticData,
  useCreateAdminAnalyticData,
  useUpdateAdminAnalyticData,
  useDeleteAdminAnalyticData
} from 'api/hooks/use-admin-analytics-data'
import { useAdminTags, useCreateAdminTag } from 'api/hooks/use-admin-tags'
import { useAdminSections } from 'api/hooks/use-admin-sections'
import renderDescription from 'shared/render-description'

const AnalyticDataForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id
  const templateRef = useRef(null)
  const pendingCursorRef = useRef(null)

  const { data: analytic, isLoading } = useAdminAnalyticData({ id })
  const { data: sections } = useAdminSections()
  const { data: tags } = useAdminTags()
  const createMutation = useCreateAdminAnalyticData({
    options: { onSuccess: (data) => navigate(`/admin/analytics-data/${data.id}`) }
  })
  const updateMutation = useUpdateAdminAnalyticData({ id, options: { onSuccess: () => navigate(`/admin/analytics-data/${id}`) } })
  const deleteMutation = useDeleteAdminAnalyticData({ options: { onSuccess: () => navigate('/admin/analytics-data') } })
  const createTagMutation = useCreateAdminTag({})

  const [formData, setFormData] = useState({
    key: '',
    name: '',
    description: '',
    risk: '',
    section_id: '',
    code: '',
    is_core: false,
    risk_score: 0,
    description_template: '',
    filters: '',
    tag_ids: []
  })

  const [newTagName, setNewTagName] = useState('')
  const [newTagCategory, setNewTagCategory] = useState('general')
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    if (analytic) {
      setShowPreview(false)
      setFormData({
        key: analytic.key || '',
        name: analytic.name || '',
        description: analytic.description || '',
        risk: analytic.risk || '',
        section_id: analytic.section_id || '',
        code: analytic.code || '',
        is_core: analytic.is_core || false,
        risk_score: analytic.risk_score || 0,
        description_template: analytic.description_template || '',
        filters: analytic.filters || '',
        tag_ids: analytic.tags?.map((t) => t.id) || []
      })
    }
  }, [analytic])

  useEffect(() => {
    if (pendingCursorRef.current !== null && templateRef.current) {
      templateRef.current.focus()
      templateRef.current.setSelectionRange(pendingCursorRef.current, pendingCursorRef.current)
      pendingCursorRef.current = null
    }
  }, [formData.description_template])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleTagToggle = (tagId) => {
    setFormData((prev) => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter((id) => id !== tagId)
        : [...prev.tag_ids, tagId]
    }))
  }

  const handleCreateTag = () => {
    if (!newTagName.trim()) return
    createTagMutation.mutate(
      { name: newTagName.trim(), category: newTagCategory },
      {
        onSuccess: (data) => {
          setFormData((prev) => ({ ...prev, tag_ids: [...prev.tag_ids, data.id] }))
          setNewTagName('')
        }
      }
    )
  }

  const insertTag = (tag) => {
    const textarea = templateRef.current
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const currentValue = formData.description_template
    const newValue = currentValue.substring(0, start) + tag + currentValue.substring(end)
    pendingCursorRef.current = start + tag.length
    setFormData((prev) => ({ ...prev, description_template: newValue }))
  }

  const buildPreviewText = () => {
    const template = formData.description_template
    if (!template) return null
    return template.replace(/\{\{(config:)?(\w+)\}\}/g, (match, configPrefix, key) => {
      if (configPrefix) {
        return `[cfg]${key}[/cfg]`
      }
      const atc = analytic?.analytic_table_columns?.find((c) => c.placeholder_key === key)
      return `[col]${atc?.table_column_name || key}[/col]`
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const submitData = { ...formData }
    if (submitData.section_id === '') submitData.section_id = null
    if (isEditing) {
      updateMutation.mutate(submitData)
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this analytic?')) {
      deleteMutation.mutate(id)
    }
  }

  const error = createMutation.error || updateMutation.error || deleteMutation.error
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading

  const tableColumns = analytic?.analytic_table_columns || []
  const configKeys = analytic?.config_keys || []

  if (isLoading && isEditing) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div>
      <h2>{isEditing ? 'Edit Analytic' : 'Create Analytic'}</h2>

      {error && <Alert variant="danger" className="mt-3">{error.message || 'An error occurred'}</Alert>}

      <Card className="mt-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Key *</Form.Label>
                  <Form.Control type="text" name="key" value={formData.key} onChange={handleChange} required placeholder="e.g. THIRDLINE-580" />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Code</Form.Label>
                  <Form.Control type="text" name="code" value={formData.code} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Section *</Form.Label>
                  <Form.Select name="section_id" value={formData.section_id} onChange={handleChange} required>
                    <option value="">Select Section...</option>
                    {sections?.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name} ({section.abbreviation})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Risk Score</Form.Label>
                  <Form.Control type="number" step="0.1" name="risk_score" value={formData.risk_score} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3 mt-4">
                  <Form.Check type="checkbox" name="is_core" label="Is Core (on by default)" checked={formData.is_core} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" value={formData.description} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Risk</Form.Label>
              <Form.Control as="textarea" rows={2} name="risk" value={formData.risk} onChange={handleChange} />
            </Form.Group>

            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Description Template</h6>
                <Button
                  variant={showPreview ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  <FontAwesomeIcon icon={showPreview ? faCode : faEye} className="me-1" />
                  {showPreview ? 'Edit' : 'Preview'}
                </Button>
              </Card.Header>
              <Card.Body>
                {isEditing && (tableColumns.length > 0 || configKeys.length > 0) && (
                  <div className="mb-3">
                    <Form.Text className="d-block mb-2 text-muted">
                      Click a tag to insert it at the cursor position in the template:
                    </Form.Text>
                    <div className="d-flex flex-wrap gap-1">
                      {tableColumns.map((atc) => (
                        <Button
                          key={atc.id}
                          variant="outline-info"
                          size="sm"
                          onClick={() => insertTag(`{{${atc.placeholder_key}}}`)}
                          title={`Column: ${atc.table_column_name}`}
                        >
                          <FontAwesomeIcon icon={faTable} className="me-1" size="xs" />
                          {`{{${atc.placeholder_key}}}`}
                        </Button>
                      ))}
                      {configKeys.map((configKey) => (
                        <Button
                          key={configKey}
                          variant="outline-warning"
                          size="sm"
                          onClick={() => insertTag(`{{config:${configKey}}}`)}
                          title={`Config: ${configKey}`}
                        >
                          <FontAwesomeIcon icon={faSlidersH} className="me-1" size="xs" />
                          {`{{config:${configKey}}}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {showPreview ? (
                  <div className="border rounded p-3 bg-light" style={{ minHeight: '60px' }}>
                    {formData.description_template ? (
                      <span>{renderDescription(buildPreviewText())}</span>
                    ) : (
                      <span className="text-muted">No template text to preview</span>
                    )}
                  </div>
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description_template"
                    value={formData.description_template}
                    onChange={handleChange}
                    ref={templateRef}
                    placeholder="e.g. Flags {{col_key}} anomalies. Threshold is {{config:duplicate_invoice_days}} days."
                  />
                )}

                <Form.Text className="text-muted">
                  Use {'{{placeholder_key}}'} for table columns and {'{{config:key}}'} for configuration values.
                  When set, this template is used instead of the plain description for municipalities.
                </Form.Text>
              </Card.Body>
            </Card>

            <Form.Group className="mb-3">
              <Form.Label>Filters</Form.Label>
              <Form.Control as="textarea" rows={2} name="filters" value={formData.filters} onChange={handleChange} />
            </Form.Group>

            <Card className="mb-3">
              <Card.Header><h6 className="mb-0">Tags</h6></Card.Header>
              <Card.Body>
                <div className="mb-3">
                  {tags?.map((tag) => (
                    <Badge
                      key={tag.id}
                      bg={formData.tag_ids.includes(tag.id) ? 'primary' : 'light'}
                      text={formData.tag_ids.includes(tag.id) ? 'white' : 'dark'}
                      className="me-2 mb-2"
                      style={{ cursor: 'pointer', border: '1px solid #dee2e6' }}
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      {tag.name} ({tag.category})
                    </Badge>
                  ))}
                </div>
                <Row>
                  <Col md={5}>
                    <Form.Control
                      type="text"
                      placeholder="New tag name..."
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      size="sm"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Select size="sm" value={newTagCategory} onChange={(e) => setNewTagCategory(e.target.value)}>
                      <option value="general">General</option>
                      <option value="fraud">Fraud</option>
                      <option value="family">Family</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleCreateTag}
                      disabled={!newTagName.trim() || createTagMutation.isLoading}
                    >
                      {createTagMutation.isLoading ? 'Creating...' : 'Create Tag'}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button type="submit" variant="primary" disabled={isSubmitting} className="me-2">
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="light-primary" onClick={() => navigate(isEditing ? `/admin/analytics-data/${id}` : '/admin/analytics-data')} disabled={isSubmitting}>Cancel</Button>
              </div>
              {isEditing && (
                <Button type="button" variant="danger" onClick={handleDelete} disabled={isSubmitting}>Delete Analytic</Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default AnalyticDataForm
